import { memo, useContext, useEffect, Suspense } from 'react';
import { UserContext } from '@app/providers';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useParams } from 'react-router';
import { ContentSkeleton, ErrorState } from '@shared/ui';
import { AppShell } from '@widgets/app-shell';
import ErrorBoundary from '_legacy/components/Shared/ErrorBoundary';
import { fetchTableSettings, REQUEST_STATUS } from 'redux_store';
import { qhsDebug } from '@shared/lib/qhsDebug';
import { isRoutePermitted } from '@shared/lib/isRoutePermitted';
import { findUnsafeRouteId } from '@shared/lib/pathSegment';
import { ALL_ROUTES } from '@app/router';

/**
 * Pre-render gate for every authenticated route. Two responsibilities:
 *
 *   1. Send the user to /login when `isLogin === false`, and hold up
 *      the children until auth context (`user`, `metadata`) populates.
 *
 *   2. Hold up tables-aware routes until `state.table.settings` lands
 *      from the server. The whole shared Table primitive reads the
 *      settings shape directly (column order / visibility / pinning /
 *      density per table key), so rendering before they arrive throws.
 *
 * Why settings can be `null` even when the user is logged in:
 *   The original dispatch lives in `App.jsx` inside
 *   `useEffect(() => { if (isLogin && !user) dispatch(...); }, [isLogin])`.
 *   It is gated on `!user`, which races against `getUser()` /
 *   `getMetadata()`: if the user slice repopulates before this effect
 *   fires, `!user` is false and the dispatch is silently skipped. Even
 *   when the dispatch DOES fire, `apiCallWithRetry` (3x with 1 s
 *   backoff) can still bottom out on a transient 401 / CORS / DNS
 *   flake — `status` lands at `failed`, `settings` stays `null`, and
 *   nothing here ever notices.
 *
 *   The user-visible symptom: every authenticated route renders the
 *   center loader forever. Side-bar nav clicks update the URL +
 *   breadcrumbs (those don't gate on settings), but the gate below
 *   keeps the actual page hidden until a hard reload re-runs the
 *   App.jsx effect.
 *
 * The fix:
 *   - When `isLogin && !settings && status === idle`, dispatch from
 *     here. Covers the race where App.jsx's effect was skipped.
 *   - When `status === failed`, swap the forever-loader for the
 *     LoadingErrorWidget — the operator gets a visible error + manual
 *     retry button instead of a silent hang.
 */
const ProtectedRoute = memo(({ ...rest }) => {
  const dispatch = useDispatch();
  const { user, isLogin, metadata, hipaaCheckParams, hipaaPassedAccountUuid } = useSelector(
    state => state.user
  );
  const { settings, status: tableSettingsStatus } = useSelector(state => state.table);
  const { changeAccountLoading, checkHipaa } = useContext(UserContext);
  const {
    additional: { component: Component, permission: requiredPermission },
  } = rest;
  const accountPermissions = metadata?.currentAccountPermissions;
  const childAccountPermissions = metadata?.childAccountPermissions;
  // DS child-account context: the route pattern carries `/accounts/:accountUuid`
  // (a Distribution Sponsor managing a child account). In that case the gating
  // permission may live on the child account, not the acting DS account.
  const hasChildContext =
    typeof rest.path === 'string' && rest.path.includes('/accounts/:accountUuid');

  // Safety-net fetch. Fires only when `status` is still `idle` — i.e.
  // App.jsx's gated dispatch never ran. Once anyone (including this
  // hook) dispatches, status moves to `loading` → `succeeded` /
  // `failed`, and the guard below stops re-firing. We deliberately do
  // NOT auto-retry on `failed` from this effect: the thunk already
  // wraps three internal retries via `apiCallWithRetry`, and a fourth
  // automatic round would just thrash.
  useEffect(() => {
    if (
      isLogin &&
      !settings &&
      tableSettingsStatus === REQUEST_STATUS.idle &&
      !changeAccountLoading
    ) {
      dispatch(fetchTableSettings());
    }
  }, [isLogin, settings, tableSettingsStatus, changeAccountLoading, dispatch]);

  const { pathname } = useLocation();
  const params = useParams();

  // HIPAA gate. The API does not enforce the agreement, so no page of an
  // account opens until this tab has confirmed that account's agreement.
  // Sign-in and account switch run the check before landing here; a reload
  // or a new tab arrives with nothing confirmed, so the check starts from
  // here and the page waits behind the loader (the dialogs render from
  // UserProvider, above the routes).
  const currentAccountUuid = metadata?.currentAccount?.uuid;
  const hipaaPending = Boolean(
    isLogin && user && currentAccountUuid && currentAccountUuid !== hipaaPassedAccountUuid
  );
  useEffect(() => {
    if (hipaaPending && !hipaaCheckParams && !changeAccountLoading) {
      // Bring the user back to this page once the check passes; the
      // catch-all route has no page of its own to return to.
      const returnRoute =
        rest.path && rest.path !== '*'
          ? { path: rest.path, params, title: rest.additional?.title }
          : undefined;
      checkHipaa(user, metadata, returnRoute);
    }
  }, [hipaaPending, hipaaCheckParams, changeAccountLoading]);

  // Navigation-driven retry. A `failed` status is sticky in Redux, so
  // without this every route the user clicks keeps rendering the same
  // error frame (URL changes, UI doesn't) until they spot the manual
  // Retry button. A nav click is an explicit user action — treat it
  // like pressing Retry: one re-fetch per path change, no loops (the
  // effect re-runs only on the next navigation).
  useEffect(() => {
    if (
      isLogin &&
      !settings &&
      tableSettingsStatus === REQUEST_STATUS.failed &&
      !changeAccountLoading
    ) {
      dispatch(fetchTableSettings());
    }
    // Deliberately keyed on pathname only — a status/settings dep
    // would re-fire outside of navigation and defeat the one-shot
    // retry-per-nav semantics.
  }, [pathname]);

  if (
    changeAccountLoading ||
    isLogin === undefined ||
    (isLogin && user === null) ||
    (isLogin && user === undefined) ||
    (isLogin && user && metadata === null) ||
    (isLogin && user && metadata === undefined)
  ) {
    // Tag exactly which condition matched so we can read the timeline
    // and tell apart "auth still loading" (changeAccountLoading) from
    // "user not yet" / "metadata not yet" failure modes.
    qhsDebug('ProtectedRoute:gate:authLoader', {
      changeAccountLoading,
      isLogin,
      hasUser: !!user,
      hasMetadata: !!metadata,
    });
    return null;
  }

  // Redirect to login if user is not logged in
  if (isLogin === false) {
    return <Navigate to="/login" replace />;
  }

  if (hipaaPending) {
    qhsDebug('ProtectedRoute:gate:hipaaPending', {
      currentAccountUuid,
      hipaaPassedAccountUuid,
      checkRunning: !!hipaaCheckParams,
    });
    return null;
  }

  // Pages put record ids from the URL straight into credentialed API paths,
  // and react-router decodes '%2F' / '%3F' inside a param. A crafted link
  // must not mount a page whose requests would hit another API route.
  const unsafeRouteId = findUnsafeRouteId(params);
  if (unsafeRouteId) {
    qhsDebug('ProtectedRoute:gate:unsafeRouteId', { path: rest.path, param: unsafeRouteId });
    return <Navigate to={ALL_ROUTES.notFound.path} replace />;
  }

  // Permission gate. Sidebar already hides nav items the user lacks
  // permission for, but a direct URL still lands on this component.
  // Without this redirect the route renders, the API returns empty,
  // and the user sees a "You do not have permission" toast on a blank
  // page. Bouncing to "/" lets DashboardRoute pick the first allowed
  // route via findAllowedRoute.
  if (
    !isRoutePermitted({
      requiredPermission,
      currentAccountPermissions: accountPermissions,
      childAccountPermissions,
      hasChildContext,
    })
  ) {
    qhsDebug('ProtectedRoute:gate:permissionDenied', {
      path: rest.path,
      requiredPermission: String(requiredPermission),
      hasChildContext,
    });
    return <Navigate to="/" replace />;
  }

  if (rest.skipDashboardCommonLayout) {
    return (
      <ErrorBoundary>
        <Suspense fallback={null}>
          <Component {...rest} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // Resolve the gate state once so the JSX below stays readable.
  let gateBody;
  if (settings) {
    qhsDebug('ProtectedRoute:gate:render', {
      tableSettingsStatus,
      settingsKeys: Object.keys(settings).length,
    });
    gateBody = <Component {...rest} />;
  } else if (tableSettingsStatus === REQUEST_STATUS.failed) {
    qhsDebug('ProtectedRoute:gate:tableSettingsError', { tableSettingsStatus });
    // Visible error + manual retry — better than a silent forever-loader.
    gateBody = (
      <ErrorState fullHeight onReload={() => dispatch(fetchTableSettings())} />
    );
  } else {
    qhsDebug('ProtectedRoute:gate:tableSettingsLoading', { tableSettingsStatus });
    gateBody = <ContentSkeleton />;
  }

  return (
    <AppShell>
      <div
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <ErrorBoundary>
          <Suspense fallback={<ContentSkeleton />}>{gateBody}</Suspense>
        </ErrorBoundary>
      </div>
    </AppShell>
  );
});

ProtectedRoute.propTypes = {};

export default ProtectedRoute;
